import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import { RomFooterProvider } from "@/context/RomFooterContext";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import RomsOverview from "@/pages/RomsOverview";
import Calculator from "@/pages/Calculator";
import Estimates from "@/pages/Estimates";
import Clients from "@/pages/Clients";
import RateSchedule from "@/pages/RateSchedule";
import Documents from "@/pages/Documents";
import Calibration from "@/pages/Calibration";
import DataGaps from "@/pages/DataGaps";
import Settings from "@/pages/Settings";
import ShareView from "@/pages/ShareView";
import TimesheetEmployees from "@/pages/timesheets/Employees";
import CaptureTime from "@/pages/timesheets/CaptureTime";
import TimesheetApprovals from "@/pages/timesheets/Approvals";
import TimeEntries from "@/pages/timesheets/TimeEntries";
import Subcontractors from "@/pages/timesheets/Subcontractors";
import SiteRegister from "@/pages/timesheets/SiteRegister";
import ProjectBoard from "@/pages/projects/Board";
import ProjectDetail from "@/pages/projects/ProjectDetail";
import SalesBoard from "@/pages/crm/SalesBoard";
import AccountDetail from "@/pages/crm/AccountDetail";
import Contracts from "@/pages/crm/Contracts";
import CrmDashboard from "@/pages/crm/CrmDashboard";
import ScheduleCalendar from "@/pages/schedules/Calendar";
import PublicClockIn from "@/pages/PublicClockIn";
import ChecklistTemplates from "@/pages/field/ChecklistTemplates";

function Shell({ children }) {
    const { user } = useAuth();
    if (!user) return children;
    return <Layout>{children}</Layout>;
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <RomFooterProvider>
                    <Routes>
                        <Route path="/login" element={<Login />} />
                        <Route path="/share/:token" element={<ShareView />} />
                        <Route path="/clock-in" element={<PublicClockIn />} />
                        <Route
                            path="/dashboard"
                            element={<ProtectedRoute><Shell><Dashboard /></Shell></ProtectedRoute>}
                        />
                        <Route
                            path="/roms-overview"
                            element={<ProtectedRoute><Shell><RomsOverview /></Shell></ProtectedRoute>}
                        />
                        <Route
                            path="/estimates"
                            element={<ProtectedRoute><Shell><Estimates /></Shell></ProtectedRoute>}
                        />
                        <Route
                            path="/calculator"
                            element={<Navigate to="/estimates" replace />}
                        />
                        {/* A single Route (not two) so navigating from a fresh
                            "new" job to its freshly-saved real id (which happens
                            mid-conversation via auto-save) only updates the
                            :jobId param in place, instead of unmounting and
                            remounting the whole Calculator/AIEstimator tree
                            (which used to wipe the AI chat's local message
                            history — see PRD 2026-07-02 "AI chat wiped on
                            first message" fix). Calculator.jsx already treats
                            jobId === "new" as "no job loaded yet". */}
                        <Route
                            path="/calculator/:jobId"
                            element={<ProtectedRoute><Shell><Calculator /></Shell></ProtectedRoute>}
                        />
                        <Route
                            path="/jobs"
                            element={<Navigate to="/estimates" replace />}
                        />
                        <Route
                            path="/clients"
                            element={<ProtectedRoute><Shell><Clients /></Shell></ProtectedRoute>}
                        />
                        <Route
                            path="/crm"
                            element={<ProtectedRoute><Shell><SalesBoard /></Shell></ProtectedRoute>}
                        />
                        <Route
                            path="/crm/dashboard"
                            element={<ProtectedRoute><Shell><CrmDashboard /></Shell></ProtectedRoute>}
                        />
                        <Route
                            path="/crm/accounts/:id"
                            element={<ProtectedRoute><Shell><AccountDetail /></Shell></ProtectedRoute>}
                        />
                        <Route
                            path="/contracts"
                            element={<ProtectedRoute><Shell><Contracts /></Shell></ProtectedRoute>}
                        />
                        <Route
                            path="/rate-schedule"
                            element={<ProtectedRoute><Shell><RateSchedule /></Shell></ProtectedRoute>}
                        />
                        <Route
                            path="/documents"
                            element={<ProtectedRoute><Shell><Documents /></Shell></ProtectedRoute>}
                        />
                        <Route
                            path="/calibration"
                            element={<ProtectedRoute><Shell><Calibration /></Shell></ProtectedRoute>}
                        />
                        <Route
                            path="/insights"
                            element={<Navigate to="/roms-overview" replace />}
                        />
                        <Route
                            path="/timesheets/capture"
                            element={<ProtectedRoute><Shell><CaptureTime /></Shell></ProtectedRoute>}
                        />
                        <Route
                            path="/timesheets/approvals"
                            element={<ProtectedRoute><Shell><TimesheetApprovals /></Shell></ProtectedRoute>}
                        />
                        <Route
                            path="/timesheets/employees"
                            element={<ProtectedRoute><Shell><TimesheetEmployees /></Shell></ProtectedRoute>}
                        />
                        <Route
                            path="/timesheets/entries"
                            element={<ProtectedRoute><Shell><TimeEntries /></Shell></ProtectedRoute>}
                        />
                        <Route
                            path="/timesheets/subcontractors"
                            element={<ProtectedRoute><Shell><Subcontractors /></Shell></ProtectedRoute>}
                        />
                        <Route
                            path="/timesheets/site-register"
                            element={<ProtectedRoute><Shell><SiteRegister /></Shell></ProtectedRoute>}
                        />
                        <Route
                            path="/projects"
                            element={<ProtectedRoute><Shell><ProjectBoard /></Shell></ProtectedRoute>}
                        />
                        <Route
                            path="/projects/:id"
                            element={<ProtectedRoute><Shell><ProjectDetail /></Shell></ProtectedRoute>}
                        />
                        <Route
                            path="/schedules"
                            element={<ProtectedRoute><Shell><ScheduleCalendar /></Shell></ProtectedRoute>}
                        />
                        <Route
                            path="/field/checklists"
                            element={<ProtectedRoute><Shell><ChecklistTemplates /></Shell></ProtectedRoute>}
                        />
                        <Route
                            path="/data-gaps"
                            element={<ProtectedRoute><Shell><DataGaps /></Shell></ProtectedRoute>}
                        />
                        <Route
                            path="/settings"
                            element={<ProtectedRoute><Shell><Settings /></Shell></ProtectedRoute>}
                        />
                        <Route path="/" element={<Navigate to="/dashboard" replace />} />
                        <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    </Routes>
                    </RomFooterProvider>
                </AuthProvider>
            </BrowserRouter>
            <Toaster position="top-right" richColors />
        </div>
    );
}

export default App;
